import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Device } from '../../lib/types';
import { rescueSetupReminderSmsQuery, type RescueSetupReminderSmsData } from '../../lib/rescueQueries';
import { formatPhoneNumber } from '../../lib/utils';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import FiNumberNotice from './FiNumberNotice';
import SmsComposer from './SmsComposer';
import r from './StalledReminders.module.scss';

interface SendSmsModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onSendSms: (phoneNumber: string, message: string) => void;
}

function getRecipient(device: Device) {
  // Foster → use holder info; Pending/Completed adoption → use adopter info
  if (device.holderType === 'foster') {
    return {
      name: device.holderName,
      firstName: device.holderFirstName,
      phone: device.holderPhone,
    };
  }
  return {
    name: device.adopterName,
    firstName: device.adopterFirstName,
    phone: device.adopterPhone,
  };
}

export default function SendSmsModal({ open, onClose, device, onSendSms }: SendSmsModalProps) {
  const { data, loading } = useQuery<RescueSetupReminderSmsData>(rescueSetupReminderSmsQuery, {
    variables: { firstName: device ? getRecipient(device).firstName : null, petName: device?.petName ?? null },
    skip: !open || !device,
  });
  // Null until the rescue edits it, so the field starts from the reminder as soon as it loads.
  const [edited, setEdited] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEdited(null);
    }
  }, [open, device]);

  if (!device) {
    return null;
  }

  const recipient = getRecipient(device);
  const reminder = data?.rescueSetupReminderSms;
  const message = edited ?? reminder?.body ?? '';

  if (!recipient.phone) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalTitle>Send SMS</ModalTitle>
        <ModalSubtitle>No phone number on file for {recipient.name || 'this recipient'}.</ModalSubtitle>
      </Modal>
    );
  }

  if (device.smsOptedOut) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalTitle>Send SMS</ModalTitle>
        <ModalSubtitle>
          {recipient.name || 'This recipient'} replied STOP and has opted out of texts from Fi.
        </ModalSubtitle>
      </Modal>
    );
  }

  function handleSend() {
    onSendSms(recipient.phone!, message);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Send SMS</ModalTitle>
      <ModalSubtitle>
        To: {recipient.name || 'Unknown'} · {formatPhoneNumber(recipient.phone)}
      </ModalSubtitle>
      {reminder ? (
        <SmsComposer body={message} onChange={setEdited} optOutLine={reminder.optOutLine} />
      ) : (
        <p className={r.statusMessage}>
          {loading ? 'Loading the message…' : "Couldn't load the message. Close this and try again."}
        </p>
      )}
      <FiNumberNotice recipients={device.holderType === 'foster' ? 'Fosters' : 'Adopters'} />
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSend} disabled={!reminder || !message.trim()}>
          Send SMS
        </Button>
      </ModalActions>
    </Modal>
  );
}
